import { inject, injectable } from 'inversify'
import {
  type CadRenderInvalidator,
  NOOP_CAD_RENDER_INVALIDATOR,
} from '@/plugins/cad/module/CADModule/renderInvalidation'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { CadI18nService, CadTranslateParams } from '@/plugins/cad/module/i18n/CadI18nService'

/**
 * 鼠标提示的内置标识集合，用于在不同流程中定位并更新同一条 tip。
 */
export enum MouseTipId {
  /** 绘制流程使用的提示 */
  Draw = 'DrawMouseTipId',
  /** 编辑流程使用的提示 */
  Edit = 'EditMouseTipId',
}

type CadTranslateKey = string
type MouseTipIcon = 'disabled' | 'x'

/** 自定义 tip 渲染函数 */
type MouseTipRenderer = (
  translate: (key: CadTranslateKey, params?: CadTranslateParams) => string
) => HTMLElement

/** 纯文本 tip 数据 */
interface ITextTipData {
  type: 'text'
  element: HTMLElement
  key: CadTranslateKey
  params?: CadTranslateParams
}

/** 自定义渲染 tip 数据 */
interface ICustomTipData {
  type: 'custom'
  element: HTMLElement
  render: MouseTipRenderer
}

/** 图标 tip 数据 */
interface IIconTipData {
  type: 'icon'
  element: HTMLElement
  icon: MouseTipIcon
}

type TipData = ITextTipData | ICustomTipData | IIconTipData

/** 鼠标提示管理器 */
@injectable()
export class MouseTipHelper {
  private container = document.createElement('div')
  private idToTipMap: Map<string, TipData> = new Map()
  private _unsubscribeI18n: (() => void) | null = null
  private lastTransform = ''
  private lastPointerPosition: { x: number; y: number } | null = null

  constructor(
    @inject(TYPES.CadI18nService) private readonly i18nService: CadI18nService,
    @inject(TYPES.RenderInvalidator)
    private readonly renderInvalidator: CadRenderInvalidator = NOOP_CAD_RENDER_INVALIDATOR
  ) {
    this.container.classList.add('mouse-tip-container')
    Object.assign(this.container.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      pointerEvents: 'none',
      display: 'inline-flex',
      alignItems: 'center',
    })
    this._unsubscribeI18n = this.i18nService.subscribe(() => this.refreshTips())
  }

  /**
   * 将提示容器挂载到给定父节点。
   *
   * @param parent - 承载提示容器的 DOM 节点
   */
  mount(parent: HTMLElement) {
    if (this.container.parentElement !== parent) {
      parent.appendChild(this.container)
      this.refreshTransform()
      this.renderInvalidator.invalidate('cad.mouseTip.mounted')
    }
  }

  /** 从父节点卸载提示容器 */
  unmount() {
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container)
      this.renderInvalidator.invalidate('cad.mouseTip.unmounted')
    }
  }

  /** 释放资源：取消 i18n 订阅并清空所有 tip */
  dispose() {
    if (this._unsubscribeI18n) {
      this._unsubscribeI18n()
      this._unsubscribeI18n = null
    }
    if (this.idToTipMap.size > 0) {
      this.container.innerHTML = ''
      this.idToTipMap.clear()
      this.renderInvalidator.invalidate('cad.mouseTip.disposed')
    }
  }

  /**
   * 添加文本 tip。
   *
   * @param id - tip 唯一标识
   * @param key - 翻译键
   * @param params - 翻译参数，可选
   */
  addTip(id: string, key: CadTranslateKey, params?: CadTranslateParams) {
    if (this.idToTipMap.has(id)) {
      this.updateTip(id, key, params)
      return
    }
    const el = this.createTipElement(this.translate(key, params))
    this.container.appendChild(el)
    this.idToTipMap.set(id, { type: 'text', element: el, key, params })
    this.refreshTransform()
    this.renderInvalidator.invalidate('cad.mouseTip.added')
  }

  /**
   * 更新文本 tip。
   *
   * @param id - tip 唯一标识
   * @param key - 翻译键
   * @param params - 翻译参数，可选
   */
  updateTip(id: string, key: CadTranslateKey, params?: CadTranslateParams) {
    if (!key) {
      return
    }
    const tipData = this.idToTipMap.get(id)
    if (!tipData) {
      return
    }
    // 从 custom 切换到 text 时需要替换 element
    if (tipData.type !== 'text') {
      const nextElement = this.createTipElement(this.translate(key, params))
      this.replaceTipElement(tipData.element, nextElement)
      this.idToTipMap.set(id, { type: 'text', element: nextElement, key, params })
      this.refreshTransform()
      this.renderInvalidator.invalidate('cad.mouseTip.updated')
      return
    }
    const nextText = this.translate(key, params)
    if (
      tipData.element.textContent === nextText &&
      tipData.key === key &&
      tipData.params === params
    ) {
      return
    }
    tipData.key = key
    tipData.params = params
    tipData.element.textContent = nextText
    this.refreshTransform()
    this.renderInvalidator.invalidate('cad.mouseTip.updated')
  }

  /**
   * 移除 tip。
   *
   * @param id - 要移除的 tip 标识
   * @param expectedKey - 可选的当前文本 key，不匹配时不移除
   */
  removeTip(id: string, expectedKey?: CadTranslateKey) {
    const tipData = this.idToTipMap.get(id)
    if (expectedKey !== undefined && (tipData?.type !== 'text' || tipData.key !== expectedKey)) {
      return
    }
    if (tipData) {
      this.container.removeChild(tipData.element)
      this.idToTipMap.delete(id)
      this.refreshTransform()
      this.renderInvalidator.invalidate('cad.mouseTip.removed')
    }
  }

  /**
   * 获取 tip 对应的 DOM 元素。
   *
   * @param id - tip 唯一标识
   * @returns 对应的 DOM 元素，未注册时返回 undefined
   */
  getTip(id: string) {
    return this.idToTipMap.get(id)?.element
  }

  /**
   * 添加自定义渲染的 tip。
   *
   * @param id - tip 唯一标识
   * @param render - 渲染函数，接收翻译器并返回 DOM 元素
   */
  addCustomTip(id: string, render: MouseTipRenderer) {
    if (this.idToTipMap.has(id)) {
      this.updateCustomTip(id, render)
      return
    }
    const element = render(this.translate.bind(this))
    this.container.appendChild(element)
    this.idToTipMap.set(id, { type: 'custom', element, render })
    this.refreshTransform()
    this.renderInvalidator.invalidate('cad.mouseTip.added')
  }

  /**
   * 添加图标 tip。
   *
   * @param id - tip 唯一标识
   * @param icon - 图标类型
   */
  addIconTip(id: string, icon: MouseTipIcon) {
    if (this.idToTipMap.has(id)) {
      this.updateIconTip(id, icon)
      return
    }
    const element = this.createIconTipElement(icon)
    this.container.appendChild(element)
    this.idToTipMap.set(id, { type: 'icon', element, icon })
    this.refreshTransform()
    this.renderInvalidator.invalidate('cad.mouseTip.added')
  }

  /**
   * 更新自定义渲染的 tip。
   *
   * @param id - tip 唯一标识
   * @param render - 渲染函数，接收翻译器并返回 DOM 元素
   */
  updateCustomTip(id: string, render: MouseTipRenderer) {
    const tipData = this.idToTipMap.get(id)
    if (tipData) {
      const element = render(this.translate.bind(this))
      this.replaceTipElement(tipData.element, element)
      this.idToTipMap.set(id, { type: 'custom', element, render })
      this.refreshTransform()
      this.renderInvalidator.invalidate('cad.mouseTip.updated')
    }
  }

  /**
   * 更新图标 tip。
   *
   * @param id - tip 唯一标识
   * @param icon - 图标类型
   */
  updateIconTip(id: string, icon: MouseTipIcon) {
    const tipData = this.idToTipMap.get(id)
    if (!tipData) {
      return
    }
    if (tipData.type === 'icon' && tipData.icon === icon) {
      return
    }
    const element = this.createIconTipElement(icon)
    this.replaceTipElement(tipData.element, element)
    this.idToTipMap.set(id, { type: 'icon', element, icon })
    this.refreshTransform()
    this.renderInvalidator.invalidate('cad.mouseTip.updated')
  }

  /**
   * 更新容器位置。
   *
   * @param options - 包含当前指针屏幕坐标的更新参数
   */
  updateTransform(options: { position: { x: number; y: number } }) {
    this.lastPointerPosition = { ...options.position }
    this.refreshTransform()
  }

  /** 使用最后一次指针坐标和当前提示尺寸重新约束容器位置。 */
  private refreshTransform() {
    const position = this.lastPointerPosition
    if (!position) {
      return
    }
    const offset = 10
    let x = position.x + offset
    let y = position.y + offset
    const parent = this.container.parentElement
    if (parent) {
      const maxX = Math.max(0, parent.clientWidth - this.container.offsetWidth - offset)
      const maxY = Math.max(0, parent.clientHeight - this.container.offsetHeight - offset)
      x = Math.min(x, maxX)
      y = Math.min(y, maxY)
    }
    const transform = `translate(${x}px,${y}px)`
    if (transform === this.lastTransform) {
      return
    }
    this.lastTransform = transform
    this.container.style.transform = transform
    this.renderInvalidator.invalidate('cad.mouseTip.transform')
  }

  /** 翻译器变更时刷新所有 tip */
  private refreshTips() {
    this.idToTipMap.forEach((tipData) => {
      if (tipData.type === 'text') {
        tipData.element.textContent = this.translate(tipData.key, tipData.params)
        this.renderInvalidator.invalidate('cad.mouseTip.updated')
        return
      }
      if (tipData.type === 'icon') {
        return
      }
      // custom 类型重新渲染
      const nextElement = tipData.render(this.translate.bind(this))
      this.replaceTipElement(tipData.element, nextElement)
      tipData.element = nextElement
      this.renderInvalidator.invalidate('cad.mouseTip.updated')
    })
    this.refreshTransform()
  }

  /** 替换 tip 元素 */
  private replaceTipElement(currentElement: HTMLElement, nextElement: HTMLElement) {
    if (currentElement === nextElement) {
      return
    }
    if (currentElement.parentElement === this.container) {
      this.container.replaceChild(nextElement, currentElement)
      return
    }
    this.container.appendChild(nextElement)
  }

  private translate(key: CadTranslateKey, params?: CadTranslateParams): string {
    return this.i18nService.translate(key, params)
  }

  private createTipElement(tip: string) {
    const el = document.createElement('span')
    el.textContent = tip
    el.className = 'mouse-tip-item'
    el.style.cssText = 'background-color: #fff; color: black; padding: 4px; font-size: 12px;'
    return el
  }

  private createIconTipElement(icon: MouseTipIcon) {
    const el = document.createElement('span')
    el.className = `mouse-tip-icon mouse-tip-icon-${icon}`
    Object.assign(el.style, {
      width: '16px',
      height: '16px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff',
      borderRadius: '50%',
      position: 'absolute',
      left: '0',
      top: '0',
      transform: 'translate(-18px, -30px)',
      boxSizing: 'border-box',
    })

    if (icon === 'disabled') {
      const circle = document.createElement('span')
      Object.assign(circle.style, {
        width: '12px',
        height: '12px',
        border: '1.5px solid #111',
        borderRadius: '50%',
        display: 'block',
        boxSizing: 'border-box',
      })

      const slash = document.createElement('span')
      Object.assign(slash.style, {
        width: '12px',
        height: '1.5px',
        backgroundColor: '#111',
        position: 'absolute',
        transform: 'rotate(-45deg)',
        transformOrigin: 'center',
      })

      el.appendChild(circle)
      el.appendChild(slash)
    }

    if (icon === 'x') {
      Object.assign(el.style, {
        backgroundColor: 'transparent',
        borderRadius: '0',
        width: '14px',
        height: '14px',
      })

      const lineStyles = {
        width: '11px',
        height: '2px',
        backgroundColor: '#ff4d4f',
        position: 'absolute',
        borderRadius: '1px',
        transformOrigin: 'center',
      }

      const line1 = document.createElement('span')
      Object.assign(line1.style, {
        ...lineStyles,
        transform: 'rotate(45deg)',
      })

      const line2 = document.createElement('span')
      Object.assign(line2.style, {
        ...lineStyles,
        transform: 'rotate(-45deg)',
      })

      el.appendChild(line1)
      el.appendChild(line2)
    }

    return el
  }
}
